import React from "react";
import { Drawer, Spin, Form, Input, Select } from "antd";
import {
  UserOutlined,
  MailOutlined,
  IdcardOutlined,
  LockOutlined,
} from "@ant-design/icons";
import FeaturePermissions from "./FeaturePermissions";
import { FormInstance } from "antd/es/form";
import { User, EmployeeOption, RoleOption, Store, FeatureData } from "../../../Types";
import {
  DRAWER_FORM_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../../components";

const { Option } = Select;

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  loading: boolean;
  editingUser: User | null;
  isCreating: boolean;
  form: FormInstance;
  availableEmployees: EmployeeOption[];
  employeeLoading: boolean;
  roles: RoleOption[];
  stores: (Store & { main_store?: number })[];
  subFeatures?: FeatureData[];
  overrides?: Record<string, number>;
  onOverridesChange?: (overrides: Record<string, number>) => void;
}

export const UserForm: React.FC<UserFormProps> = ({
  open,
  onClose,
  onSubmit,
  loading,
  isCreating,
  form,
  availableEmployees,
  employeeLoading,
  roles,
  stores,
  subFeatures,
  overrides = {},
  onOverridesChange,
}) => {
  return (
    <Drawer
      title={drawerTitle(isCreating ? "Add New User" : "Edit User")}
      open={open}
      onClose={onClose}
      width={DRAWER_FORM_WIDTH}
      footer={drawerFooter({
        actions: [
          { label: "Cancel", onClick: onClose },
          {
            label: isCreating ? "Create User" : "Save Changes",
            type: "primary",
            loading,
            onClick: onSubmit,
          },
        ],
      })}
      styles={{ body: { paddingTop: 8 } }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            label={
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Username
              </span>
            }
            name="username"
            rules={[
              { required: true, message: "Username is required." },
              { min: 3, message: "At least 3 characters." },
            ]}
          >
            <Input
              prefix={<UserOutlined className="text-gray-400" />}
              placeholder="johndoe"
              className="rounded-xl"
              disabled={!isCreating}
            />
          </Form.Item>

          <Form.Item
            label={
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Email
              </span>
            }
            name="email"
            rules={[
              { required: true, message: "Email is required." },
              { type: "email", message: "Enter a valid email." },
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-gray-400" />}
              placeholder="john@example.com"
              className="rounded-xl"
            />
          </Form.Item>

          {isCreating && (
            <Form.Item
              label={
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Employee
                </span>
              }
              name="employee_id"
              rules={[{ required: true, message: "Employee is required." }]}
            >
              <Select
                showSearch
                loading={employeeLoading}
                placeholder="Select an available employee"
                className="rounded-xl"
                optionFilterProp="label"
                notFoundContent={
                  employeeLoading
                    ? "Loading employees..."
                    : "No available employees"
                }
              >
                {(availableEmployees ?? []).map((employee) => {
                  const fullName = `${employee.first_name} ${employee.last_name}`;
                  const label = `${fullName} ${employee.nic || ""} ${
                    employee.phone || ""
                  }`;

                  return (
                    <Option key={employee.id} value={employee.id} label={label}>
                      <div className="flex items-center gap-2">
                        <IdcardOutlined className="text-gray-400" />
                        <div>
                          <p className="m-0 text-sm font-medium text-gray-700 leading-tight">
                            {fullName}
                          </p>
                        </div>
                      </div>
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}

          {isCreating && (
            <Form.Item
              label={
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Password
                </span>
              }
              name="password"
              rules={[
                { required: true, message: "Password is required." },
                { min: 6, message: "At least 6 characters." },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-gray-400" />}
                placeholder="Min. 6 characters"
                className="rounded-xl"
              />
            </Form.Item>
          )}

          <Form.Item
            label={
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Role
              </span>
            }
            name="role_id"
            rules={[{ required: true, message: "Role is required." }]}
          >
            <Select placeholder="Select a role" className="rounded-xl">
              {(roles ?? []).map((r) => (
                <Option key={r.id} value={r.id}>
                  {r.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Assigned Store
              </span>
            }
            name="store_id"
            rules={[{ required: true, message: "Please assign a store." }]}
          >
            <Select placeholder="Select a store" className="rounded-xl">
              {stores?.map((store) => (
                <Option key={store.id} value={store.id}>
                  {store.name} {store.main_store === 1 ? "(Main)" : ""}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        {subFeatures && subFeatures.length > 0 && onOverridesChange && (
          <FeaturePermissions
            subFeatures={subFeatures}
            overrides={overrides}
            onOverridesChange={onOverridesChange}
            isCreating={isCreating}
          />
        )}
      </Spin>
    </Drawer>
  );
};
